import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

export default function DragDropMultiUpload({
  accept,
  maxSizeMB = 10,
  maxFiles = 20,
  onChange,
  onUpload,
  disabled = false,
}) {
  const defaultAccept = useMemo(
    () => ({
      "image/*": [],
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
    }),
    []
  );

  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState([]);
  const { t } = useTranslation();

  const maxSize = maxSizeMB * 1024 * 1024;

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      const newErrors = [];

      // De-duplicate by name+size
      const existingKey = (f) => `${f.name}-${f.size}`;
      const existing = new Set(files.map(existingKey));

      const filtered = [];
      for (const f of acceptedFiles) {
        if (existing.has(existingKey(f))) {
          newErrors.push(`${f.name}: already added.`);
          continue;
        }
        filtered.push(
          Object.assign(f, {
            preview: f.type.startsWith("image/")
              ? URL.createObjectURL(f)
              : null,
          })
        );
      }

      // Handle rejections (size/type)
      for (const r of rejectedFiles) {
        const file = r.file;
        const reasons = r.errors.map((e) => e.message).join(", ");
        newErrors.push(`${file.name}: ${reasons}`);
      }

      // Respect maxFiles
      const remainingSlots = Math.max(0, maxFiles - files.length);
      const toAdd = filtered.slice(0, remainingSlots);
      if (filtered.length > remainingSlots) {
        newErrors.push(
          `You can only add ${maxFiles} files in total. Extra files were ignored.`
        );
      }

      setFiles((prev) => [...prev, ...toAdd]);
      setErrors(newErrors);
    },
    [files, maxFiles]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: accept || defaultAccept,
    maxSize,
    multiple: true,
    noClick: true, // we use our own button
    disabled,
  });

  useEffect(() => {
    if (onChange) onChange(files);
  }, [files, onChange]);

  // Clean up object URLs
  useEffect(() => {
    return () =>
      files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
  }, [files]);

  const removeAt = (idx) => {
    setFiles((prev) => {
      const c = [...prev];
      const [removed] = c.splice(idx, 1);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return c;
    });
  };

  const clearAll = () => {
    files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setFiles([]);
  };

  const totalSize = files.reduce((acc, f) => acc + (f.size || 0), 0);

  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h5 className="mb-0">{t('upload_files')}</h5>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={open}
              disabled={disabled}
            >
              {t('browse')}
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => onUpload && onUpload(files)}
              disabled={disabled || files.length === 0}
            >
              {t('upload')} {files.length > 0 ? `(${files.length})` : ""}
            </button>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={clearAll}
              disabled={disabled || files.length === 0}
            >
              {t('clear')}
            </button>
          </div>
        </div>

        {/* Drop zone */}
        <div
          {...getRootProps({
            className:
              "w-100 p-4 rounded-3 bg-light text-center mb-3 position-relative",
          })}
          style={{
            border: "2px dashed #ced4da",
            transition: "background-color 0.2s, border-color 0.2s",
            backgroundColor: isDragActive ? "#e9f5ff" : undefined,
            borderColor: isDragActive ? "#0d6efd" : "#ced4da",
            cursor: disabled ? "not-allowed" : "pointer",
            minHeight: 140,
          }}
        >
          <input {...getInputProps()} />
          <div className="d-flex flex-column align-items-center justify-content-center h-100">
            <div className="fw-medium mb-1">
              {isDragActive ? "Drop files here…" : "Drag & drop files here"}
            </div>
            <div className="text-muted small">{t('click_browse_button')}</div>
            <div className="text-muted small mt-2">
             {t('max_size_per_file')}: {maxSizeMB} MB • {t('max_files')}: {maxFiles}
            </div>
          </div>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div className="alert alert-warning py-2">
            <ul className="mb-0 ps-3">
              {errors.map((e, i) => (
                <li key={i} className="small">
                  {e}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Files List */}
        {files.length > 0 ? (
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="small text-muted">
                {t('selected')}: <strong>{files.length}</strong> • {t('total_size')}:{" "}
                {formatBytes(totalSize)}
              </div>
            </div>

            <div className="row g-3">
              {files.map((file, idx) => (
                <div
                  key={`${file.name}-${file.size}-${idx}`}
                  className="col-12 col-md-6 col-lg-4"
                >
                  <div className="border rounded-3 p-2 h-100 d-flex gap-2">
                    <div
                      style={{ width: 56, height: 56 }}
                      className="flex-shrink-0 rounded overflow-hidden bg-white d-flex align-items-center justify-content-center border"
                    >
                      {file.preview ? (
                        <img
                          src={file.preview}
                          alt={file.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          loading="lazy"
                        />
                      ) : (
                        <span
                          className="bi bi-file-earmark-text fs-4"
                          aria-hidden="true"
                        />
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div
                        className="fw-semibold text-truncate"
                        title={file.name}
                      >
                        {file.name}
                      </div>
                      <div className="text-muted small">
                        {formatBytes(file.size)}
                      </div>
                      <div className="mt-2 d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeAt(idx)}
                        >
                          {t('remove')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center text-muted small">
          {t('no_files_selected')}
          </div>
        )}
      </div>
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 Bytes";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
}